import { Component, Input } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { SharedModule } from '@sbd-account-portal/app/shared/shared.module';
import { SharedComponentsModule } from '@sbd-doug-frontend/shared/lib/shared-components/shared-components.module';
import {
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet,
  CIPXQuoteRequestIntakeFormStateBondDetailedQuestion,
  CIPXQuoteRequestIntakeFormStateBondQuestionSetCollection,
} from '../intake-form.types';
import { createCollectionItemGroup, isQuestionVisible } from '../intake-form.helpers';
import { CIPXQuoteRequestIntakeFormQuestionControlComponent } from '../question-control/question-control.component';

/**
 * Renders a collection-layout question set (e.g. probate wards) as a
 * repeatable list of item groups backed by a FormArray keyed by the
 * question_set_code inside additional_questions.
 *
 * The questionSet input is expected to have per-set overrides already merged
 * into its questions (see getCollectionSetsForSection).
 */
@Component({
  standalone: true,
  imports: [
    SharedModule,
    SharedComponentsModule,
    CIPXQuoteRequestIntakeFormQuestionControlComponent
  ],
  selector: 'cipx-quote-request-intake-form-question-collection',
  styleUrls: ['./question-collection.component.scss'],
  templateUrl: 'question-collection.component.html'
})
export class CIPXQuoteRequestIntakeFormQuestionCollectionComponent {
  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;
  @Input() questionSet!: CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet;

  get items(): FormArray<FormGroup> {
    return this.quoteRequestForm.controls.additional_questions.get(
      this.questionSet.question_set_code
    ) as FormArray<FormGroup>;
  }

  get collection(): CIPXQuoteRequestIntakeFormStateBondQuestionSetCollection {
    return this.questionSet.question_set.collection ?? {};
  }

  get minItems(): number {
    return Math.max(1, this.collection.min_items ?? 1);
  }

  get maxItems(): number | null {
    return this.collection.max_items ?? null;
  }

  get canAdd(): boolean {
    return this.maxItems === null || this.items.length < this.maxItems;
  }

  get canRemove(): boolean {
    return this.items.length > this.minItems;
  }

  get addItemLabel(): string {
    return this.collection.add_item_label ?? 'Add another';
  }

  get hasValidationErrorBeforeAdd(): boolean {
    if (!this.items.length) {
      return false;
    }

    const lastItem = this.getItemGroup(this.items.length - 1);
    for (let index = this.questionSet.questions.length - 1; index >= 0; index--) {
      const question = this.questionSet.questions[index];
      const control = lastItem.get(question.question_code);
      if (control && isQuestionVisible(question, lastItem)) {
        return control.invalid && control.touched;
      }
    }

    return false;
  }

  getItemGroup(index: number): FormGroup {
    return this.items.at(index);
  }

  getItemLabel(index: number): string {
    const label = this.collection.item_label ?? this.questionSet.question_set_label;
    return this.items.length > 1 ? `${label} ${index + 1}` : label;
  }

  addItem(): void {
    if (this.canAdd) {
      this.items.push(createCollectionItemGroup(this.questionSet.questions));
    }
  }

  removeItem(index: number): void {
    if (this.canRemove) {
      this.items.removeAt(index);
    }
  }

  isVisible(question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion, index: number): boolean {
    return isQuestionVisible(question, this.getItemGroup(index));
  }
}
